import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import { UserControls, CustomIcon } from '@p81-common/ui';

import SettingsLink from 'components/Layout/parts/MainHeader/parts/SettingsLink/SettingsLink';
import HelpCenter from 'components/Layout/parts/MainHeader/parts/HelpCenter/HelpCenter';
import BreadCrumbs from 'components/Layout/parts/MainHeader/parts/BreadCrumbs/BreadCrumbs';
import NotificationsButton from './parts/NotificationsButton/NotificationsButton';

import classes from './MainHeader.module.scss';
import createColumnClasses from './styles';
import { RegionSwitcher } from '../../../common/shared/RegionSwitcher/RegionSwitcher';
import { hasAccess, resources, access } from 'utils/permissions';
import { jarvisRoleNames } from 'data/constants';
import { GlobalSearch } from '../../../GlobalSearch/GlobalSearch';
import { postRequest } from 'utils/requests';
import { env } from 'env';
import { CompanyIcon, Avatar } from '../../../common/Elements/Elements';
import { getInitials } from 'utils/common';
import {
  globalSearchCategories,
  appBgColorByProtocol,
  handleSetSearchPlaceholder,
  mapUserData,
  mapNetworkData,
  mapAccountData,
  mapApplicationData,
  mapGatewayData
} from './globalSearch';
import { useTheme } from '@mui/styles';

const MainHeader = ({
  headerProps,
  toggleNotificationsOpen,
  unreadCount,
  userControlsProps
}) => {
  const { breadcrumbsProps } = headerProps;
  const [searchResults, setSearchResults] = useState([]);
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const [totalResultsCount, setTotalResultsCount] = useState(0);
  const theme = useTheme();
  const columns = createColumnClasses();

  const userObject = {
    ...userControlsProps,
    userRole: jarvisRoleNames[userControlsProps.userRole] || ''
  };

  const handleGlobalSearch = (selectedCategory, value, page = 1, limit) => {
    const searchValue = value.trim();

    if (searchValue.length > 2) {
      setGlobalSearchLoading(true);
      postRequest(
        '/customers/search',
        {
          filterArea: selectedCategory,
          filter: value
        },
        {
          params: { page, limit }
        }
      )
        .then(res => {
          if (page === 1) {
            setSearchResults(
              mapResults(selectedCategory, res.body.data || res.body)
            );
          } else {
            setSearchResults(prevResults => [
              ...prevResults,
              ...mapResults(selectedCategory, res.body.data || res.body)
            ]);
          }
          setTotalResultsCount(res.body.totalRecords);
          setGlobalSearchLoading(false);
        })
        .catch(err => {
          console.log(err);
          setGlobalSearchLoading(false);
        });
    } else if (searchValue.length === 0) {
      setSearchResults([]);
      setTotalResultsCount(0);
    }
  };

  const mapResults = (category, results) => {
    switch (category) {
      case 'account':
        return results.map(result => ({
          ...mapAccountData(result),
          icon: {
            component: <CompanyIcon domain={result.website} />
          }
        }));
      case 'user':
        return results.map(result => ({
          ...mapUserData(result),
          icon: {
            component: (
              <Avatar
                className={classes.avatar}
                initials={getInitials(result.firstName, result.lastName)}
                initialsColor={result.initialscolor}
              />
            )
          }
        }));
      case 'network':
        return results.map(result => ({
          ...mapNetworkData(result),
          icon: {
            iconSrc: `${env.REACT_APP_STATIC_ASSETS_URL}/icons2/icon_networks.svg`
          }
        }));
      case 'gateway':
        return results.map(result => ({
          ...mapGatewayData(result),
          icon: {
            iconSrc: `${env.REACT_APP_STATIC_ASSETS_URL}/icons2/icon_gateways.svg`
          }
        }));
      case 'application':
        return results.map(result => ({
          ...mapApplicationData(result),
          icon: {
            component: (
              <CustomIcon
                src={`${env.REACT_APP_STATIC_ASSETS_URL}/icons2/icon_${result.protocol}.svg`}
                className={classes.applicationIcon}
                style={{
                  backgroundColor:
                    theme.palette.specialColors[
                      appBgColorByProtocol[result.protocol]
                    ]
                }}
              />
            )
          }
        }));
      default:
        return [];
    }
  };

  const settingsAccess = hasAccess([resources.SETTINGS_USERS, access.READ]);
  const location = useLocation();
  const showRegionSwitcher = !location.pathname.startsWith('/settings');
  if (location.pathname.indexOf('login') !== -1) {
    return null;
  } else {
    return (
      <div className={columns.header}>
        <div className={columns.colStart}>
          <BreadCrumbs crumbs={breadcrumbsProps.customBreadcrumbs} />
        </div>
        {/* Omitted entirely rather than rendered empty: an empty flex item
            would still take a gap on each side and push the bar off centre. */}
        {showRegionSwitcher && (
          <div className={columns.colCenter}>
            <RegionSwitcher />
          </div>
        )}
        <div className={columns.colEnd}>
          <GlobalSearch
            selectOptions={globalSearchCategories}
            handleTextChange={handleGlobalSearch}
            results={searchResults}
            loading={globalSearchLoading}
            setSearchPlaceholder={handleSetSearchPlaceholder}
            onCategoryChange={() => {
              setSearchResults([]);
              setTotalResultsCount(0);
            }}
            totalRecords={totalResultsCount}
          />
          {settingsAccess && <SettingsLink />}
          <HelpCenter />
          <NotificationsButton
            toggleNotificationsOpen={toggleNotificationsOpen}
            unreadCount={unreadCount}
          />
          <UserControls {...userObject} classes={classes} />
        </div>
      </div>
    );
  }
};

MainHeader.propTypes = {
  headerProps: PropTypes.object,
  toggleNotificationsOpen: PropTypes.func,
  unreadCount: PropTypes.number,
  userControlsProps: PropTypes.object
};

export default MainHeader;
